import { addDays, type LocalDate } from '../dates';
import type { DateRange } from '../ledger/counting';
import { monthEnd, monthStart, weekStartOf, type WeekStart } from '../spending/range';
import { monthRange, type MonthKey } from '../spending/trends';
import {
  DEFAULT_SALARY_SCHEDULE,
  NO_HOLIDAYS,
  occurrencesInRange,
  type IncomeSourceLike,
} from './schedule';

/**
 * Cutoffs run from one payday to the day before the next (the Philippine "cutoff"). They come from
 * the user's first salary source; with none, from the default semi-monthly schedule (15th and last day).
 */
export const FALLBACK_CUTOFF_SOURCE: IncomeSourceLike = {
  id: '__default_cutoffs__',
  name: '',
  kind: 'salary',
  schedule: DEFAULT_SALARY_SCHEDULE,
  defaultAmounts: [0, 0],
  weekendRule: 'before',
  startDate: '1900-01-01',
  categoryId: null,
  deletedAt: null,
};

/**
 * `sources` in the order the user added them; the first live salary source sets the cutoffs
 * (Ivan, 2026-11-02: fine for one job).
 */
export function pickCutoffSource(sources: readonly IncomeSourceLike[]): IncomeSourceLike {
  return sources.find((s) => s.kind === 'salary' && s.deletedAt === null) ?? FALLBACK_CUTOFF_SOURCE;
}

export interface Cutoff {
  /** 1-based position among the cutoffs whose payday falls in the month. */
  index: number;
  range: DateRange;
}

/** Payday dates (weekend rule applied) between from and to, ignoring the source's start and end dates. */
function cutoffStarts(
  source: IncomeSourceLike,
  from: LocalDate,
  to: LocalDate,
  holidays: ReadonlySet<LocalDate>,
): LocalDate[] {
  const unbounded = { ...source, startDate: '1900-01-01', endDate: null, deletedAt: null };
  return occurrencesInRange(unbounded, { start: from, end: to }, holidays).map((o) => o.date);
}

/** Looks this far around a date for paydays; covers monthly and every-two-weeks schedules. */
const SEARCH_DAYS = 70;

/** The cutoff (payday to the day before the next payday) that contains `date`. */
export function cutoffContaining(
  date: LocalDate,
  source: IncomeSourceLike,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): DateRange {
  const starts = cutoffStarts(
    source,
    addDays(date, -SEARCH_DAYS),
    addDays(date, SEARCH_DAYS),
    holidays,
  );
  const start = [...starts].reverse().find((d) => d <= date) ?? monthStart(date);
  const next = starts.find((d) => d > date);
  return { start, end: next ? addDays(next, -1) : monthEnd(date) };
}

/** The cutoffs whose payday falls in `month`, in order. */
export function cutoffsOfMonth(
  month: MonthKey,
  source: IncomeSourceLike,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): Cutoff[] {
  const m = monthRange(month);
  const starts = cutoffStarts(source, m.start, addDays(m.end, SEARCH_DAYS), holidays);
  return starts
    .filter((d) => d <= m.end)
    .map((start, i) => {
      const next = starts[i + 1];
      return { index: i + 1, range: { start, end: next ? addDays(next, -1) : monthEnd(start) } };
    });
}

export type FundPeriod = 'weekly' | 'per_cutoff' | 'monthly';

/** The period of a fund that contains `date`. */
export function periodRange(
  period: FundPeriod,
  date: LocalDate,
  opts: {
    weekStart: WeekStart;
    cutoffSource: IncomeSourceLike;
    holidays?: ReadonlySet<LocalDate>;
  },
): DateRange {
  switch (period) {
    case 'weekly': {
      const start = weekStartOf(date, opts.weekStart);
      return { start, end: addDays(start, 6) };
    }
    case 'monthly':
      return { start: monthStart(date), end: monthEnd(date) };
    case 'per_cutoff':
      return cutoffContaining(date, opts.cutoffSource, opts.holidays ?? NO_HOLIDAYS);
  }
}
